<template>
  <v-select
    :model-value="subscriptionTypes"
    :items="planItems"
    :menu-props="{ maxHeight: '320', width: 275 }"
    class="filter-menu"
    rounded
    menu-icon="icon-arrow-down-xs"
    :class="{ 'filter-active': subscriptionTypes?.length }"
    :placeholder="$t('manageWorkspaces.filters.plan')"
    density="compact"
    variant="outlined"
    item-value="value"
    item-title="title"
    multiple
    :disabled="disabled"
    data-testid="subscription-types-select"
    @update:model-value="$emit('update:subscriptionTypes', $event)"
  >
    <template #selection="{ index }">
      <span v-if="index === 0" class="body2">{{ $t("manageWorkspaces.filters.plan") }}</span>
    </template>
    <template #label>
      <span v-if="subscriptionTypes.length" class="filter-label">
        {{ subscriptionTypes.length }}
      </span>
    </template>
    <template #item="{ props }">
      <v-list-item v-bind="props">
        <template v-slot:prepend="{ isActive }">
          <v-list-item-action start>
            <v-checkbox-btn density="compact" :model-value="isActive"></v-checkbox-btn>
          </v-list-item-action>
        </template>
      </v-list-item>
    </template>
    <template #append-item v-if="subscriptionTypes.includes(SubscriptionType.TRIAL)">
      <v-divider class="my-1"></v-divider>
      <v-list-item
        :title="$t('manageWorkspaces.filters.expiringWithin7Days')"
        @click="$emit('update:nearExpirationOnly', !nearExpirationOnly)"
        data-testid="near-expiration-switch-item"
      >
        <template #prepend>
          <v-list-item-action start>
            <v-switch
              :model-value="nearExpirationOnly"
              @update:model-value="$emit('update:nearExpirationOnly', $event)"
              @click.stop
              color="primary"
              inset
              :ripple="false"
              density="compact"
              hide-details
            >
            </v-switch>
          </v-list-item-action>
        </template>
      </v-list-item>
    </template>
  </v-select>
</template>

<script lang="ts">
import { computed, defineComponent, watch, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import { SubscriptionType } from "@/constants/workplaces";

export default defineComponent({
  props: {
    subscriptionTypes: {
      type: Array as PropType<SubscriptionType[]>,
      required: true,
    },
    nearExpirationOnly: {
      type: Boolean,
      required: true,
    },
    plans: {
      type: Array as PropType<SubscriptionType[]>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:subscriptionTypes", "update:nearExpirationOnly"],
  setup(props, { emit }) {
    const { t } = useI18n();

    const planItems = computed(() =>
      props.plans.map((value) => ({
        value,
        title: t(`subscriptions.plans.${value}`),
      }))
    );

    // Auto-reset nearExpirationOnly when the TRIAL plan is deselected or all plans are cleared,
    // since the "expiring within 7 days" toggle only applies to trial workspaces.
    watch(
      () => props.subscriptionTypes,
      (newValue, oldValue) => {
        const trialWasRemoved =
          oldValue.includes(SubscriptionType.TRIAL) && !newValue.includes(SubscriptionType.TRIAL);
        if (newValue.length === 0 || trialWasRemoved) {
          emit("update:nearExpirationOnly", false);
        }
      },
      { deep: true }
    );

    return {
      SubscriptionType,
      planItems,
    };
  },
});
</script>

<style scoped lang="scss">
.filter-menu {
  width: 120px;
}
</style>
